import React from "react";
import {
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TableHead,
  Paper,
  TableFooter,
  TablePagination,
  IconButton,
  Button,
  Box,
  SvgIcon,
  Tooltip
} from "@mui/material";
import { JOB_LIMIT } from "../../../helpers/constants";
import TablePaginationActions from "./TablePaginationActions";
import { Link, useNavigate } from "react-router-dom";
import { formatDateTime } from "../../../helpers/cookies";
import { getStageDisplayName } from "../../../helpers/stageDisplay";
import SendIcon from "@mui/icons-material/Send";

const EditSvgIcon = (props) => (
  <SvgIcon {...props}>
    <g fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
      <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z" />
    </g>
  </SvgIcon>
);

const parseDateString = (raw) => {
  if (!raw) return null;
  let s = String(raw).trim();
  s = s.replace(/^(\d{4}-\d{2}-\d{2})\s+/, '$1T');
  s = s.replace(/(\.\d{3})\d+/, '$1');
  s = s.replace(/([+-])(\d{2})(\d{2})$/, '$1$2:$3');
  s = s.replace(/\s+([+-]\d{2}:\d{2})$/, '$1');
  if (/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}:\d{2}(\.\d+)?$/.test(s)) {
    s += 'Z';
  }

  const date = new Date(s);
  if (isNaN(date.getTime())) {
    console.warn('[RowColor] parseDateString: invalid date after normalisation', { raw, normalised: s });
    return null;
  }
  return date;
};

const getRowColor = (row) => {
  const rawStage =
    row._stage_name ||
    row.stage_name ||
    row.status ||
    row.Status ||
    row.stage ||
    row.Stage ||
    '';

  const stage = String(rawStage).toLowerCase().trim();

  const isTracked =
    stage.includes('progress') ||
    stage.replace(/[-_\s]+/g, ' ').includes('in progress') ||
    stage.includes('correction') ||
    stage.includes('revision');

  if (!isTracked) return null;

  const dateStr =
    row.stage_updated_at ||
    row._updated_at ||
    row.updated_at ||
    row['Last Updated'] ||
    row.updated_date ||
    null;

  const date = parseDateString(dateStr);

  if (!date) {
    console.warn('[RowColor] No valid date for row', {
      id: row.id || row.virtual_request || row['Req#'],
      stage,
      dateStr,
    });
    return null;
  }

  const now = new Date();
  const diffMinutes = (now - date) / (1000 * 60);

  let color = null;

  if (diffMinutes > 720) {
    color = {
      border: '#FFFF00',
      tooltip: `Job is in "${rawStage}" for more than 12 hours`,
    };
  } else if (diffMinutes > 240) {
    color = {
      border: '#2cb4cc',
      tooltip: `Job is in "${rawStage}" for more than 4 hours`,
    };
  }

  if (process.env.NODE_ENV !== 'production' || color) {
    console.log('[RowColor]', {
      id: row.id || row.virtual_request,
      stage: rawStage,
      dateStr,
      diffHours: (diffMinutes / 60).toFixed(2),
      color: color ?? 'none',
    });
  }

  return color;
};

function JobTable({
  data = [],
  loading = false,
  total = 0,
  page = 0,
  onPageChange,
  job_id,
  onSendProof,
  selected_process_tab,
  company_process,
  columns: providedColumns = []
}) {
  const navigate = useNavigate();
  const dataKeys =
    data.length > 0 ? Object.keys(data[0]).filter(k => !k.startsWith("_")) : [];


  const columns = [
    ...providedColumns,
    ...dataKeys.filter((k) => !providedColumns.includes(k) && k !== 'id'),
  ].filter(col => col !== 'id');
  const columnLabels = {
    virtual_request: "Req#",
    quotes: "Quote#",
    submitted_date: "Submitted Date",
    stage: "Status",
    job_flag: "Job Flag",
    customer_name: "Customer Name",
    proof_request_type: "Request Type",
    logo_name: "Logo Name",
    compaign_name: "Campaign",
    rep_number: "Rep Number"
  };
  const fieldRank = {
    "virtual_request": 1, "Req#": 1,
    "quotes": 2, "Quote#": 2,
    "submitted_date": 3, "Submitted Date": 3,
    "stage": 4, "Status": 4,
    "job_flag": 5, "Job Flag": 5,
    "customer_name": 6, "Customer Name": 6,
    "proof_request_type": 7, "Request Type": 7,
    "logo_name": 8, "Logo Name": 8,
    "compaign_name": 9, "Campaign": 9,
    "rep_number": 10, "Rep Number": 10
  };

  columns.sort((a, b) => {
    const ra = fieldRank[a];
    const rb = fieldRank[b];
    const aIn = ra !== undefined;
    const bIn = rb !== undefined;

    if (aIn && bIn) return ra - rb;
    if (aIn) return -1;
    if (bIn) return 1;
    return 0;
  });

  const getFileName = (url) => {
    if (!url) return "Unknown File";
    try {
      return decodeURIComponent(url.split("/").pop());
    } catch {
      return url.split("/").pop();
    }
  };

  // Display-level label remapping for the Status column via shared helper.
  // "Requested" → "New"; add more mappings in src/helpers/stageDisplay.js
  const getCellValue = (col, row) => {
    const raw = row[col];
    const isStatusCol = col === "stage" || (columnLabels[col] || "").toLowerCase() === "status";
    if (isStatusCol && typeof raw === "string") {
      return getStageDisplayName(raw);
    }
    return raw;
  };

  const getColumnWidth = (col) => {
    const lowerCol = col.toLowerCase().trim();
    const label = (columnLabels[col] || col).toLowerCase().trim();

    if (lowerCol === 'id') return '60px';
    if (lowerCol === 'virtual_request' || label === 'req#') return '80px';
    if (lowerCol === 'quotes' || label === 'quote#') return '80px';
    if (lowerCol === 'customer_name' || label === 'customer name') return '200px';
    if (lowerCol === 'logo_name' || label === 'logo name') return '150px';
    if (lowerCol.includes('compaign') || lowerCol === 'campaign' || label === 'campaign') return '150px';
    if (lowerCol.includes('reference') || lowerCol === 'ref_no' || label.includes('ref')) return '160px';
    if (lowerCol.includes('status') || lowerCol === 'stage' || label === 'status') return '140px';
    if (lowerCol.includes('priority') || label === 'priority') return '110px';
    if (lowerCol.includes('service') || lowerCol.includes('category') || label.includes('request type')) return '170px';
    if (lowerCol === 'description' || label === 'description') return '350px';
    if (lowerCol.includes('submit') || lowerCol.includes('date') || lowerCol.includes('updated') || label.includes('date')) return '150px';

    return '130px';
  };

  const getProcessSettings = (processId) => {
    if (!company_process) return { standard: false, annotation: false };
    const process = company_process.find(p => p.id == processId);
    return {
      standard: process?.standard_proof || false,
      annotation: process?.annotated_proof || false
    };
  };


  return (
    <Paper sx={{ width: "100%", overflow: "hidden", boxShadow: 0 }}>
      <TableContainer sx={{ maxHeight: { xl: 800, md: 440 } }}>
        <Table
          stickyHeader
          size="small"
          sx={{
            tableLayout: "auto",
            px: 2,
            borderCollapse: "separate",
            borderSpacing: 0,
          }}
        >
          <TableHead>
            {/* ... existing TableHead content ... */}
            <TableRow>
              {columns.map((col) => (
                <TableCell
                  key={col}
                  sx={{
                    backgroundColor: "#fff",
                    color: "#4b5563",
                    fontSize: "16px",
                    fontWeight: 600,
                    whiteSpace: "nowrap",
                    fontFamily: "'Inter Tight', sans-serif",
                    borderBottom: "2px solid gray",
                    textAlign: "left",
                    pl: 1.5,
                    pr: 1.5,
                    width: getColumnWidth(col),
                    minWidth: getColumnWidth(col),
                    maxWidth: (col === "Description" || columnLabels[col] === "Description") ? getColumnWidth(col) : "unset",
                    verticalAlign: "middle",
                  }}
                >
                  {columnLabels[col] ?? col}
                </TableCell>
              ))}

              {/* Edit column */}
              <TableCell

                sx={{
                  backgroundColor: "#fff",
                  color: "#4b5563",
                  fontFamily: "'Inter Tight', sans-serif",
                  borderBottom: "2px solid gray",
                  textAlign: "center",
                  width: "100px",
                  minWidth: "100px",
                  verticalAlign: "middle",
                }}
              >

              </TableCell>
            </TableRow>
          </TableHead>

          <TableBody>
            {data.length > 0 ? (
              data.map((row, idx) => {
                const settings = getProcessSettings(row.process_id || selected_process_tab);
                const shouldShowSendProof = !!(settings.standard || settings.annotation);
                const rushRed = '#e53935';
                const jobFlag = row.job_flag || row.JobFlag || row['Job Flag'] || '';
                const isRush = String(jobFlag).toLowerCase().trim() === 'rush';
                const rowColorObj = getRowColor(row);

                return (
                  <TableRow
                    hover
                    key={idx}
                    sx={{
                      backgroundColor: idx % 2 === 0 ? "white" : "#f9f9f9",
                      cursor: "pointer",
                      transition: 'background 0.2s',
                    }}
                    onClick={() => {
                      const jobId = row.id || row["Req#"] || row["virtual_request"];
                      if (jobId) {
                        navigate(selected_process_tab ? `/job-detail/${jobId}?process_id=${selected_process_tab}` : `/job-detail/${jobId}`);
                      }
                    }}
                  >
                    {columns.map((col, colIdx) => {
                      const indicators = [];
                      if (colIdx === 0) {
                        if (isRush) indicators.push({ color: rushRed, tooltip: 'Rush Job' });
                        if (rowColorObj && rowColorObj.border) indicators.push({ color: rowColorObj.border, tooltip: rowColorObj.tooltip });
                      }

                      return (
                        <TableCell
                          key={col}
                          sx={{
                            textAlign: "left",
                            pl: 1.5,
                            pr: 1.5,
                            fontFamily: "'Inter Tight', sans-serif",
                            fontSize: "14px",
                            color: "#111827",
                            width: getColumnWidth(col),
                            minWidth: getColumnWidth(col),
                            maxWidth: (col === "Description" || columnLabels[col] === "Description") ? getColumnWidth(col) : "unset",
                            verticalAlign: "top",
                            borderBottom: "1px solid #f3f4f6",
                          }}
                        >
                          <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                            {indicators.length > 0 && (
                              <Box
                                sx={{
                                  display: 'flex',
                                  ml: -1, // Push the bar slightly more left towards the edge
                                  mr: 1.5,
                                  mt: '1px',
                                  borderRadius: '0',
                                  overflow: 'hidden',
                                  boxShadow: '0 1px 2px rgba(0,0,0,0.2)'
                                }}
                              >
                                {indicators.map((ind, i) => (
                                  <Tooltip key={i} title={ind.tooltip} arrow placement="top">
                                    <Box
                                      sx={{
                                        width: '10px',
                                        height: '30px',
                                        backgroundColor: ind.color,
                                      }}
                                    />
                                  </Tooltip>
                                ))}
                              </Box>
                            )}
                            <div style={{ flex: 1, minWidth: 0 }}>
                              {(() => {
                                if (col === "Description" || col === "description") {
                                  return (
                                    <Tooltip title={row[col]} placement="top" arrow>
                                      <div style={{
                                        wordBreak: "break-word",
                                        whiteSpace: "normal",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                        display: "-webkit-box",
                                        WebkitLineClamp: 3,
                                        WebkitBoxOrient: "vertical",
                                      }}>
                                        {row[col]}
                                      </div>
                                    </Tooltip>
                                  );
                                }

                                const tooltipKeys = [
                                  "virtual_request", "quotes", "submitted_date", "stage", 
                                  "job_flag", "customer_name", "proof_request_type", 
                                  "logo_name", "compaign_name", "rep_number",
                                  "Req#", "Quote#", "Submitted Date", "Status", 
                                  "Job Flag", "Customer Name", "Request Type", 
                                  "Logo Name", "Campaign", "Rep Number"
                                ];
                                
                                let content;
                                if (col === "Need a quote") {
                                  content = row[col] ? "Yes" : "No";
                                } else if (Array.isArray(row[col])) {
                                  content = row[col].length > 0 ? (
                                    row[col].map((fileUrl, i) => (
                                      <div key={i}>
                                        <a href={fileUrl} target="_blank" rel="noopener noreferrer">
                                          {getFileName(fileUrl)}
                                        </a>
                                      </div>
                                    ))
                                  ) : "—";
                                } else if ((col.toLowerCase().includes("date") ||
                                    col.toLowerCase().includes("submitted") ||
                                    col.toLowerCase().includes("updated") ||
                                    (typeof row[col] === 'string' && /^\d{4}-\d{2}-\d{2}/.test(row[col]))) && row[col]) {
                                  content = formatDateTime(row[col]);
                                } else {
                                  content = getCellValue(col, row) ? getCellValue(col, row) : "—";
                                }

                                const cellInner = (
                                  <div style={{
                                    wordBreak: "break-word",
                                    whiteSpace: "nowrap",
                                    overflow: "hidden",
                                    textOverflow: "ellipsis"
                                  }}>
                                    {content}
                                  </div>
                                );

                                if (tooltipKeys.includes(col) && !Array.isArray(row[col]) && content !== "—" && content != null) {
                                  return (
                                    <Tooltip title={content} placement="top" arrow>
                                      {cellInner}
                                    </Tooltip>
                                  );
                                }
                                
                                return cellInner;
                              })()}
                            </div>
                          </Box>
                        </TableCell>
                      );
                    })}

                    {/* Edit button cell */}
                    <TableCell
                      align="center"
                      sx={{
                        width: "100px",
                        minWidth: "100px",
                        verticalAlign: "top",
                      }}
                    >
                      <Box sx={{ display: "flex", gap: "6px", justifyContent: "center" }}>

                        {/* Edit icon removed */}



                        {/* Send Art To Customer/Rep Button */}
                        {row._stage_name?.toLowerCase().trim() === 'completed' && row._has_completed_file && shouldShowSendProof && (
                          <Button
                            variant="contained"
                            size="small"
                            onClick={() => onSendProof && onSendProof(row.id)}
                            sx={{
                              backgroundColor: "#ff6100",
                              color: "#fff",
                              textTransform: "none",
                              fontSize: "12px",
                              fontWeight: 600,
                              whiteSpace: "nowrap",
                              borderRadius: "6px",
                              px: 1.5,
                              py: 0.5,
                              "&:hover": { backgroundColor: "#e65500" },
                            }}
                          >
                            {row._send_art_to_customer ? "Send Art To Customer" : "Send Art To Rep"}
                          </Button>
                        )}

                      </Box>
                    </TableCell>

                  </TableRow>
                );
              })
            ) : !loading ? (
              <TableRow>
                <TableCell align="center" colSpan={columns.length + 1}>
                  No Record Found
                </TableCell>
              </TableRow>
            ) : (
              <TableRow>
                <TableCell align="center" colSpan={columns.length + 1}>
                  Loading...
                </TableCell>
              </TableRow>
            )}
          </TableBody>

          {data.length > 0 && (
            <TableFooter>
              <TableRow>
                <TableCell colSpan={columns.length + 1}>
                  <TablePagination
                    component="div"
                    count={total}
                    page={page}
                    rowsPerPage={JOB_LIMIT}
                    rowsPerPageOptions={[JOB_LIMIT]}
                    onPageChange={onPageChange}
                    ActionsComponent={TablePaginationActions}
                    labelDisplayedRows={() => null}
                  />
                </TableCell>
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>
    </Paper>
  );
}

export default JobTable;
